/**
 * Ageon — All Clients
 * Mirrors the Daewoo "All clients" view: search, column filters, column picker,
 * save/reset view, CSV + Excel export.
 * Route: /admin/clients
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Search, RefreshCw, Eye, ChevronDown,
    Download as DLIcon, SlidersHorizontal, Save,
    FileSpreadsheet, RotateCcw, GripVertical, Columns3,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { fmtPct } from '../../utils/format';

/* ─────────────────────────────── helpers ─────────────────────────────── */
const inr = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(v || 0));
const fd = (d) => {
    if (!d) return '—';
    const p = new Date(d);
    return isNaN(p) ? '—' : p.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const csvEscape = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;

/* lead age counted from submission date */
const leadDays = (createdAt) => {
    if (!createdAt) return 0;
    const t = new Date(createdAt).getTime();
    return Number.isNaN(t) ? 0 : Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
};

/* short display ID from mongo _id */
const short = (id) => (id ? String(id).slice(-6).toUpperCase() : '—');
const ageonId = (r) => `AG-${short(r._id)}-${new Date(r.createdAt).getFullYear()}`;

const getFranchiseLabel = (type) => {
    const m = {
        SignatureFico: 'Signature FICO',
        Signature: 'Signature FOFO',
        'Master Franchise': 'Master Franchise',
        'Daewoo District Partner Franchise': 'Daewoo District Partner Franchise',
    };
    return m[type] || type || '—';
};

/* ── all financial fields live inside client.data ── */
function getPaymentMetrics(client) {
    if (client.status !== 'approved') {
        return {
            finalPayable: 0,
            totalReceived: 0,
            receivedPercent: 0,
            balance: 0,
            pendingVerify: 0,
            inactive: true,
        };
    }
    const d = client.data || {};
    const finalPayable = Number(d.dealAmount || 0);
    const approvedPayments = (client.secondPayments || []).filter(
        (p) => p.status === 'approved' && !p.isRefunded && !p.promotedToToken,
    );
    const approvedTotal = approvedPayments.reduce((s, p) => s + Number(p.amount || 0), 0);
    /* Trust server rollups (promotion- and refund-aware); fall back for pre-migration rows. */
    const tokenNet = client.effectiveTokenAmount != null
        ? Number(client.effectiveTokenAmount)
        : Math.max(0, Number(d.tokenReceivedAmount || 0) - Number(client.totalRefunded || 0));
    const totalReceived = client.totalReceived != null
        ? Number(client.totalReceived)
        : tokenNet + approvedTotal;
    /* Prefer the server's receivedPercent (totalReceived / finalPayable) so every
       screen shows the same number. Fall back to a local calc for old records. */
    const receivedPercent = client.receivedPercent != null
        ? Number(client.receivedPercent)
        : (finalPayable > 0 ? (totalReceived / finalPayable) * 100 : 0);
    const balance = client.balanceAmount != null
        ? Number(client.balanceAmount)
        : Math.max(finalPayable - totalReceived, 0);
    const pendingVerify = (client.secondPayments || []).filter((p) => p.status === 'pending').length;
    return { finalPayable, totalReceived, receivedPercent, balance, pendingVerify, inactive: finalPayable <= 0 };
}

/* ──────────────────────────── sub-components ─────────────────────────── */
const PaymentTrackerCell = ({ client }) => {
    const { finalPayable, receivedPercent, pendingVerify, inactive } = getPaymentMetrics(client);
    if (inactive) return <span className="text-slate-300 text-xs">—</span>;
    return (
        <div className="flex flex-col gap-1 min-w-[8rem]">
            <div className="flex items-center gap-1.5">
                <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                    <div
                        className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500"
                        style={{ width: `${Math.min(100, receivedPercent)}%` }}
                    />
                </div>
                <span className="text-[10px] font-bold tabular-nums text-slate-700">{fmtPct(receivedPercent)}</span>
            </div>
            {pendingVerify > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                    {pendingVerify} pending
                </span>
            )}
        </div>
    );
};

/* ──────────────────────────── constants ──────────────────────────────── */
const STORAGE_KEY = 'ageon_clients_list_view_v1';

const DEFAULT_COL_VIS = {
    idx: true, date: true, clientId: true, franchise: true, client: true,
    territory: true, branch: true, phone: true, email: false, firm: false,
    createdBy: false, amount: true, tracker: true, balance: true, actions: true,
};

const EMPTY_COL_FILTERS = {
    clientId: '', franchise: '', name: '', territory: '', leadAge: '',
    branch: '', createdBy: '', firm: '',
};

/* ════════════════════════════ COMPONENT ══════════════════════════════════ */
export default function AgeonClientsList() {
    const navigate = useNavigate();

    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    /* ── filters ── */
    const [search, setSearch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [franchiseLine, setFranchiseLine] = useState('');
    const [officeBranch, setOfficeBranch] = useState('');
    const [paymentTrackFilter, setPaymentTrackFilter] = useState('');
    const [colFilters, setColFilters] = useState({ ...EMPTY_COL_FILTERS });
    const [sortCol, setSortCol] = useState('createdAt');
    const [sortDir, setSortDir] = useState('desc');

    /* ── ui ── */
    const [colVis, setColVis] = useState({ ...DEFAULT_COL_VIS });
    const [showColPanel, setShowColPanel] = useState(false);

    /* ── load ── */
    const load = async (refresh = false) => {
        refresh ? setRefreshing(true) : setLoading(true);
        try {
            const { data } = await api.get('/forms/submissions');
            setClients(data.items || []);
        } catch {
            toast.error('Failed to load clients');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    /* ── restore saved view ── */
    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const v = JSON.parse(raw);
            if (typeof v.search === 'string') setSearch(v.search);
            if (typeof v.dateFrom === 'string') setDateFrom(v.dateFrom);
            if (typeof v.dateTo === 'string') setDateTo(v.dateTo);
            if (typeof v.franchiseLine === 'string') setFranchiseLine(v.franchiseLine);
            if (typeof v.officeBranch === 'string') setOfficeBranch(v.officeBranch);
            if (typeof v.paymentTrackFilter === 'string') setPaymentTrackFilter(v.paymentTrackFilter);
            if (v.colFilters) setColFilters({ ...EMPTY_COL_FILTERS, ...v.colFilters });
            if (v.sortCol) setSortCol(v.sortCol);
            if (v.sortDir === 'asc' || v.sortDir === 'desc') setSortDir(v.sortDir);
            if (v.colVis) setColVis({ ...DEFAULT_COL_VIS, ...v.colVis });
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { load(); }, []);

    const saveView = useCallback(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                search, dateFrom, dateTo, franchiseLine, officeBranch,
                paymentTrackFilter, colFilters, sortCol, sortDir, colVis,
            }));
            toast.success('View saved — filters and columns restored next visit.');
        } catch { toast.error('Could not save view'); }
    }, [search, dateFrom, dateTo, franchiseLine, officeBranch, paymentTrackFilter, colFilters, sortCol, sortDir, colVis]);

    const resetView = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        setSearch(''); setDateFrom(''); setDateTo('');
        setFranchiseLine(''); setOfficeBranch('');
        setPaymentTrackFilter('');
        setColFilters({ ...EMPTY_COL_FILTERS });
        setColVis({ ...DEFAULT_COL_VIS });
        setSortCol('createdAt'); setSortDir('desc');
        toast.success('View reset to defaults');
    }, []);

    const onSort = (k) => {
        if (sortCol === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        else { setSortCol(k); setSortDir('asc'); }
    };

    /* ── derived lists for dropdowns — read from c.data ── */
    const serviceLines = useMemo(() => {
        const s = new Set();
        clients.forEach((c) => { if (c.data?.franchiseType) s.add(c.data.franchiseType); });
        return [...s].sort();
    }, [clients]);

    const branchOptions = useMemo(() => {
        const s = new Set();
        clients.forEach((c) => { if (c.data?.officeBranch) s.add(c.data.officeBranch); });
        return [...s].sort();
    }, [clients]);

    /* ── column-filter predicate — read from c.data ── */
    const matchesColFilters = (c) => {
        const d = c.data || {};
        if (colFilters.clientId.trim()) {
            const q = colFilters.clientId.trim().toLowerCase();
            if (!ageonId(c).toLowerCase().includes(q) && !c._id.toString().includes(q)) return false;
        }
        if (colFilters.franchise.trim()) {
            const q = colFilters.franchise.trim().toLowerCase();
            if (!getFranchiseLabel(d.franchiseType).toLowerCase().includes(q)) return false;
        }
        if (colFilters.name.trim()) {
            const q = colFilters.name.trim().toLowerCase();
            if (!(d.name || '').toLowerCase().includes(q)) return false;
        }
        if (colFilters.territory.trim()) {
            const q = colFilters.territory.trim().toLowerCase();
            const territory = `${d.franchiseCity || ''} ${d.personalCity || ''}`.trim();
            if (!territory.toLowerCase().includes(q)) return false;
        }
        if (colFilters.leadAge.trim()) {
            const raw = colFilters.leadAge.trim();
            const days = leadDays(c.createdAt);
            if (/^\d+$/.test(raw)) { if (days < Number(raw)) return false; }
            else if (!String(days).includes(raw)) return false;
        }
        if (colFilters.branch.trim()) {
            const q = colFilters.branch.trim().toLowerCase();
            if (!(d.officeBranch || '').toLowerCase().includes(q)) return false;
        }
        if (colFilters.createdBy.trim()) {
            const q = colFilters.createdBy.trim().toLowerCase();
            if (!(c.submittedBy?.name || '').toLowerCase().includes(q)) return false;
        }
        if (colFilters.firm.trim()) {
            const q = colFilters.firm.trim().toLowerCase();
            if (!(d.firmName || '').toLowerCase().includes(q)) return false;
        }
        return true;
    };

    /* ── filtered + sorted list ── */
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const from = dateFrom ? new Date(dateFrom) : null;
        const to = dateTo ? new Date(`${dateTo}T23:59:59`) : null;

        let out = clients.filter((c) => {
            if ((c.clientStatus || 'active') === 'cancelled') return false;
            const d = c.data || {};
            if (q) {
                const hay = [
                    c._id, d.name, d.phone, d.email,
                    d.franchiseCity, d.personalCity,
                    d.officeBranch, d.firmName,
                    d.franchiseType, c.submittedBy?.name,
                ].filter(Boolean).join(' ').toLowerCase();
                if (!hay.includes(q)) return false;
            }
            const created = new Date(c.createdAt);
            if (from && created < from) return false;
            if (to && created > to) return false;
            if (franchiseLine && d.franchiseType !== franchiseLine) return false;
            if (officeBranch && d.officeBranch !== officeBranch) return false;
            if (!matchesColFilters(c)) return false;
            if (paymentTrackFilter) {
                const m = getPaymentMetrics(c);
                if (m.inactive) return false;
                if (paymentTrackFilter === 'pending_verify' && m.pendingVerify === 0) return false;
                if (paymentTrackFilter === 'shortfall' && m.receivedPercent >= 100) return false;
                if (paymentTrackFilter === 'complete' && m.receivedPercent < 100) return false;
            }
            return true;
        });

        const get = (c, k) => {
            const d = c.data || {};
            const m = getPaymentMetrics(c);
            switch (k) {
                case 'createdAt': return new Date(c.createdAt).getTime();
                case 'client': return (d.name || '').toLowerCase();
                case 'phone': return d.phone || '';
                case 'city': return (d.franchiseCity || d.personalCity || '').toLowerCase();
                case 'service': return (d.franchiseType || '').toLowerCase();
                case 'budget': return Number(d.dealAmount || 0);
                case 'collect': return m.inactive ? -0.001 : m.receivedPercent;
                case 'paybalance': return m.inactive ? Infinity : m.balance;
                default: return '';
            }
        };

        out.sort((a, b) => {
            const av = get(a, sortCol), bv = get(b, sortCol);
            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
        return out;
    }, [clients, search, dateFrom, dateTo, sortCol, sortDir, paymentTrackFilter, franchiseLine, officeBranch, colFilters]);

    /* ── exports — read from c.data ── */
    const exportRow = (c) => {
        const d = c.data || {};
        const m = getPaymentMetrics(c);
        return [
            fd(c.createdAt),
            ageonId(c),
            getFranchiseLabel(d.franchiseType),
            d.name || '',
            d.franchiseCity || d.personalCity || '',
            leadDays(c.createdAt),
            d.officeBranch || '',
            c.submittedBy?.name || '',
            d.firmName || '',
            d.phone || '',
            d.email || '',
            d.dealAmount || '',
            m.inactive ? '' : fmtPct(m.receivedPercent),
            m.inactive ? '' : m.balance,
        ];
    };

    const exportCsv = () => {
        const cols = ['Submitted', 'Client ID', 'Franchise type', 'Client', 'Territory', 'Lead age (d)',
            'Office branch', 'Created by', 'Firm', 'Phone', 'Email', 'Amount', 'Collected %', 'Balance'];
        const rows = filtered.map((c) => exportRow(c).map(csvEscape).join(','));
        const blob = new Blob([['\ufeff', cols.map(csvEscape).join(','), ...rows].join('\n')],
            { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `ageon-clients-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const exportExcel = () => {
        const esc = (x) => String(x ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const headers = ['Submitted', 'Client ID', 'Franchise type', 'Client', 'Territory', 'Lead age (days)',
            'Office branch', 'Created by', 'Firm', 'Phone', 'Email', 'Amount (INR)', 'Collected %', 'Balance (INR)'];
        let html = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">'
            + '<head><meta charset="UTF-8"></head><body><table border="1"><tr>'
            + headers.map((h) => `<th>${esc(h)}</th>`).join('') + '</tr>';
        filtered.forEach((c) => {
            html += `<tr>${exportRow(c).map((v) => `<td>${esc(v)}</td>`).join('')}</tr>`;
        });
        html += '</table></body></html>';
        const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `ageon-clients-${new Date().toISOString().slice(0, 10)}.xls`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success('Excel file downloaded.');
    };

    /* ── table column count for empty row ── */
    const tableColumnCount = useMemo(() => {
        return Object.values(colVis).filter(Boolean).length + (colVis.actions !== false ? 1 : 0);
    }, [colVis]);

    const SortTh = ({ label, k, className = '' }) => (
        <th
            onClick={() => onSort(k)}
            className={`px-3 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400
                        cursor-pointer select-none whitespace-nowrap hover:text-white transition ${className}`}
        >
            <span className="inline-flex items-center gap-1">
                {label}
                {sortCol === k && <ChevronDown size={11} className={sortDir === 'asc' ? 'rotate-180' : ''} />}
            </span>
        </th>
    );

    const card = 'rounded-xl border border-slate-200/90 bg-white shadow-sm';

    /* ══════════════════════════════ RENDER ══════════════════════════════ */
    return (
        <div className="space-y-4 -mx-1 sm:-mx-2">

            {/* ── Header card ── */}
            <div className={`${card} p-5 sm:p-6`}>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 font-heading">
                            Ageon clients
                        </h1>
                        <p className="mt-1 text-sm text-slate-600">
                            <span className="font-semibold text-slate-900 tabular-nums">{filtered.length}</span>
                            {' '}client{filtered.length === 1 ? '' : 's'} shown
                            <span className="text-slate-400"> · </span>
                            <span className="tabular-nums">{clients.length}</span> loaded from server
                        </p>
                        <p className="mt-2 text-xs">
                            <Link to="/admin/payment-ledger" className="font-semibold text-teal-700 hover:text-teal-900">
                                Payment ledger →
                            </Link>
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={exportCsv}
                            className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3.5 py-2 text-xs font-bold text-white shadow-sm hover:bg-sky-700 transition">
                            <DLIcon className="h-4 w-4" /> CSV
                        </button>
                        <button type="button" onClick={exportExcel}
                            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3.5 py-2 text-xs font-bold text-white shadow-sm hover:bg-emerald-700 transition">
                            <FileSpreadsheet className="h-4 w-4" /> Excel
                        </button>
                        <button type="button" onClick={saveView}
                            className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-3.5 py-2 text-xs font-bold text-white shadow-sm hover:bg-teal-700 transition">
                            <Save className="h-4 w-4" /> Save view
                        </button>
                        <button type="button" onClick={resetView}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-slate-50 px-3.5 py-2 text-xs font-bold text-slate-700 hover:bg-slate-100 transition">
                            <RotateCcw className="h-4 w-4" /> Reset
                        </button>
                        <button type="button" onClick={() => load(true)}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition">
                            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Reload
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Search + quick filters ── */}
            <div className={`${card} p-4 sm:p-5`}>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
                    <div className="flex-1 min-w-0">
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Search</label>
                        <div className="relative">
                            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                            <input
                                className="w-full rounded-lg border border-slate-300 bg-slate-50/80 py-2.5 pl-10 pr-3 text-sm text-slate-900
                                           placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                                placeholder="Search by name, ID, phone, email, territory, firm, franchise…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 w-full lg:w-auto lg:min-w-[30rem]">
                        <div>
                            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Franchise type</label>
                            <select className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3 text-sm
                                               focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                                value={franchiseLine} onChange={(e) => setFranchiseLine(e.target.value)}>
                                <option value="">All types</option>
                                {serviceLines.map((x) => <option key={x} value={x}>{getFranchiseLabel(x)}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Branch</label>
                            <select className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3 text-sm
                                               focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                                value={officeBranch} onChange={(e) => setOfficeBranch(e.target.value)}>
                                <option value="">All branches</option>
                                {branchOptions.map((x) => <option key={x} value={x}>{x}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Collection</label>
                            <select className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3 text-sm"
                                value={paymentTrackFilter} onChange={(e) => setPaymentTrackFilter(e.target.value)}>
                                <option value="">All</option>
                                <option value="pending_verify">Pending verification</option>
                                <option value="shortfall">Below 100% vs payable</option>
                                <option value="complete">Fully collected</option>
                            </select>
                        </div>
                        <div className="relative">
                            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Columns</label>
                            <button type="button" onClick={() => setShowColPanel((v) => !v)}
                                className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300
                                           bg-white py-2.5 px-3 text-sm font-semibold text-slate-800 hover:bg-slate-50">
                                <Columns3 className="h-4 w-4 text-slate-600" /> Columns
                            </button>
                            {showColPanel && (
                                <>
                                    <button type="button"
                                        className="fixed inset-0 z-40 cursor-default bg-black/20 sm:bg-transparent"
                                        onClick={() => setShowColPanel(false)} />
                                    <div className="absolute right-0 top-full z-50 mt-1 w-[min(100vw-2rem,22rem)]
                                                    rounded-xl border border-slate-200 bg-white p-3 shadow-xl ring-1 ring-black/5">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-1 pb-2">
                                            Visible columns
                                        </p>
                                        <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
                                            {[
                                                ['idx', '# Row'], ['date', 'Added date'], ['clientId', 'Client ID'],
                                                ['franchise', 'Franchise type'], ['client', 'Client name'],
                                                ['territory', 'Territory'], ['branch', 'Branch'], ['phone', 'Phone'],
                                                ['email', 'Email'], ['firm', 'Firm'], ['createdBy', 'Created by'],
                                                ['amount', 'Amount'], ['tracker', 'Tracker'], ['balance', 'Balance'],
                                            ].map(([key, lab]) => (
                                                <label key={key} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50 cursor-pointer text-sm">
                                                    <input type="checkbox"
                                                        className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                                                        checked={colVis[key] !== false}
                                                        onChange={(e) => setColVis((prev) => ({ ...prev, [key]: e.target.checked }))} />
                                                    <span>{lab}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-3 items-end border-t border-slate-100 pt-4">
                    <div>
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">From</label>
                        <input type="date" className="rounded-lg border border-slate-300 bg-white py-2 px-3 text-sm"
                            value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">To</label>
                        <input type="date" className="rounded-lg border border-slate-300 bg-white py-2 px-3 text-sm"
                            value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </div>
                    <div className="ml-auto flex items-center gap-2 text-xs font-semibold text-slate-500 tabular-nums">
                        <SlidersHorizontal className="h-4 w-4" />
                        {filtered.length} / {clients.length}
                    </div>
                </div>
            </div>

            {/* ── Column filters ── */}
            <div className={`${card} p-4 sm:p-5`}>
                <div className="flex items-center justify-between gap-3 mb-4">
                    <h2 className="text-sm font-extrabold text-slate-900 tracking-tight">Column filters</h2>
                    <button type="button" onClick={() => setColFilters({ ...EMPTY_COL_FILTERS })}
                        className="text-xs font-bold text-sky-700 hover:text-sky-900">
                        Clear all
                    </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        ['clientId', 'Client ID', 'Filter ID…'],
                        ['franchise', 'Franchise type', 'Filter type…'],
                        ['name', 'Client name', 'Filter name…'],
                        ['territory', 'Territory', 'City / territory…'],
                        ['leadAge', 'Lead age (days)', 'e.g. 30 = 30+ days'],
                        ['branch', 'Office branch', 'Filter branch…'],
                        ['createdBy', 'Created by', 'Sales user…'],
                        ['firm', 'Firm name', 'Company / firm…'],
                    ].map(([key, lab, ph]) => (
                        <div key={key}>
                            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{lab}</label>
                            <input
                                className="w-full rounded-lg border border-slate-200 bg-slate-50/80 py-2 px-3 text-sm
                                           placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500/30"
                                placeholder={ph}
                                value={colFilters[key]}
                                onChange={(e) => setColFilters((prev) => ({ ...prev, [key]: e.target.value }))}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Data table ── */}
            <div className={`${card} overflow-hidden`}>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[800px]">
                        <thead>
                            <tr className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-left">
                                {colVis.idx && (
                                    <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500 w-10">
                                        <span className="inline-flex items-center gap-1">
                                            <GripVertical className="h-3.5 w-3.5 opacity-50" /> #
                                        </span>
                                    </th>
                                )}
                                {colVis.date && <SortTh label="Added" k="createdAt" />}
                                {colVis.clientId && <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">Client ID</th>}
                                {colVis.franchise && <SortTh label="Franchise type" k="service" />}
                                {colVis.client && <SortTh label="Client" k="client" />}
                                {colVis.territory && <SortTh label="Territory" k="city" />}
                                {colVis.branch && <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">Branch</th>}
                                {colVis.phone && <SortTh label="Phone" k="phone" />}
                                {colVis.email && <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-400 hidden lg:table-cell">Email</th>}
                                {colVis.firm && <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-400 hidden xl:table-cell">Firm</th>}
                                {colVis.createdBy && <SortTh label="Created by" k="by" className="hidden xl:table-cell" />}
                                {colVis.amount && <SortTh label="Amount" k="budget" className="text-right" />}
                                {colVis.tracker && <SortTh label="Tracker" k="collect" className="min-w-[8.5rem]" />}
                                {colVis.balance && <SortTh label="Balance" k="paybalance" className="text-right" />}
                                {colVis.actions !== false && (
                                    <th className="px-3 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-slate-400
                                                   sticky right-0 bg-slate-900 min-w-[7rem] shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.45)]">
                                        Actions
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.map((c, i) => {
                                const d = c.data || {};
                                const m = getPaymentMetrics(c);
                                return (
                                    <tr key={c._id}
                                        className="group odd:bg-white even:bg-slate-50/80 hover:bg-teal-50/40 transition-colors cursor-pointer"
                                        onClick={() => navigate(`/admin/clients/${c._id}`)}>
                                        {colVis.idx && (
                                            <td className="px-3 py-2.5 text-xs text-slate-400 font-mono tabular-nums">{i + 1}</td>
                                        )}
                                        {colVis.date && (
                                            <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap font-medium">
                                                {fd(c.createdAt)}
                                            </td>
                                        )}
                                        {colVis.clientId && (
                                            <td className="px-3 py-2.5 font-mono text-[11px] text-teal-700 tabular-nums">
                                                {ageonId(c)}
                                            </td>
                                        )}
                                        {colVis.franchise && (
                                            <td className="px-3 py-2.5 text-xs max-w-[140px]">
                                                {d.franchiseType
                                                    ? <span className="inline-flex truncate rounded-md bg-teal-50 px-2 py-0.5 font-semibold text-teal-900 ring-1 ring-teal-100">
                                                        {getFranchiseLabel(d.franchiseType)}
                                                    </span>
                                                    : <span className="text-slate-300">—</span>}
                                            </td>
                                        )}
                                        {colVis.client && (
                                            <td className="px-3 py-2.5 font-semibold text-slate-900 max-w-[160px] truncate">
                                                {d.name || '—'}
                                            </td>
                                        )}
                                        {colVis.territory && (
                                            <td className="px-3 py-2.5 text-xs text-slate-700 max-w-[120px] truncate">
                                                {d.franchiseCity || d.personalCity || '—'}
                                            </td>
                                        )}
                                        {colVis.branch && (
                                            <td className="px-3 py-2.5 text-xs text-slate-600 max-w-[100px] truncate">
                                                {d.officeBranch || '—'}
                                            </td>
                                        )}
                                        {colVis.phone && (
                                            <td className="px-3 py-2.5 text-xs font-mono text-slate-700 tabular-nums whitespace-nowrap">
                                                {d.phone || '—'}
                                            </td>
                                        )}
                                        {colVis.email && (
                                            <td className="px-3 py-2.5 text-xs text-slate-500 max-w-[160px] truncate hidden lg:table-cell">
                                                {d.email || '—'}
                                            </td>
                                        )}
                                        {colVis.firm && (
                                            <td className="px-3 py-2.5 text-xs text-slate-600 max-w-[120px] truncate hidden xl:table-cell">
                                                {d.firmName || '—'}
                                            </td>
                                        )}
                                        {colVis.createdBy && (
                                            <td className="px-3 py-2.5 text-xs text-slate-600 hidden xl:table-cell max-w-[100px] truncate">
                                                {c.submittedBy?.name || '—'}
                                            </td>
                                        )}
                                        {colVis.amount && (
                                            <td className="px-3 py-2.5 text-right text-sm font-bold tabular-nums text-slate-900 whitespace-nowrap">
                                                {d.dealAmount != null && d.dealAmount !== ''
                                                    ? inr(d.dealAmount)
                                                    : <span className="text-slate-300 font-normal text-xs">—</span>}
                                            </td>
                                        )}
                                        {colVis.tracker && (
                                            <td className="px-3 py-2.5 align-middle">
                                                <PaymentTrackerCell client={c} />
                                            </td>
                                        )}
                                        {colVis.balance && (
                                            <td className="px-3 py-2.5 text-right text-sm font-semibold tabular-nums text-slate-800 whitespace-nowrap">
                                                {m.inactive ? '—' : inr(m.balance)}
                                            </td>
                                        )}
                                        {colVis.actions !== false && (
                                            <td className="px-3 py-2.5 sticky right-0 bg-white group-even:bg-slate-50/80 group-hover:bg-teal-50/40
                                                           shadow-[-10px_0_14px_-10px_rgba(15,23,42,0.15)]"
                                                onClick={(e) => e.stopPropagation()}>
                                                <div className="flex justify-end gap-1">
                                                    <Link to={`/admin/clients/${c._id}`}
                                                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white
                                                                   px-2 py-1 text-[10px] font-bold text-slate-800 hover:border-teal-500 hover:text-teal-800">
                                                        <Eye className="h-3.5 w-3.5" /> Open
                                                    </Link>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={tableColumnCount}
                                        className="px-6 py-16 text-center text-slate-500 text-sm">
                                        {loading ? 'Loading…' : clients.length === 0 ? 'No clients yet.' : 'No rows match these filters.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}